import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline'
import { motion } from 'framer-motion'

import { api } from '../services/api'
import { PackageCard } from '../components/PackageCard'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { Button } from '../components/Button'
import { PackageCard as PackageCardType } from '../types/package'

type PackageTypeFilter = 'all' | 'media' | 'ai_corporate' | 'full_stack' | 'website_hosting' | 'hardware_bundle'

const packageTypeFilters: { value: PackageTypeFilter; label: string }[] = [
  { value: 'all', label: 'All Packages' },
  { value: 'media', label: 'Media & Entertainment' },
  { value: 'ai_corporate', label: 'Corporate AI' },
  { value: 'full_stack', label: 'Full Stack Solutions' },
  { value: 'website_hosting', label: 'Website Hosting' },
  { value: 'hardware_bundle', label: 'Hardware Bundles' },
]

export function PackagesPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedType, setSelectedType] = useState<PackageTypeFilter>('all')
  const [sortBy, setSortBy] = useState<'name' | 'price' | 'popular'>('popular')

  // Fetch all packages
  const {
    data: packages = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['packages', 'all'],
    queryFn: () => api.packages.getPackageCards({ limit: 50 }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Filter and sort packages
  const filteredAndSortedPackages = useMemo(() => {
    let filtered = packages

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(pkg =>
        pkg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.short_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.key_features?.some(feature =>
          feature.toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    }

    // Filter by package type
    if (selectedType !== 'all') {
      filtered = filtered.filter(pkg => pkg.package_type === selectedType)
    }

    // Sort packages
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name)
        case 'price':
          // Sort by monthly price, falling back to yearly, then one-time
          const getPrice = (pkg: PackageCardType) => {
            if (pkg.monthly_price_display) {
              return parseFloat(pkg.monthly_price_display.replace(/[$,]/g, ''))
            }
            if (pkg.yearly_price_display) {
              return parseFloat(pkg.yearly_price_display.replace(/[$,]/g, '')) / 12
            }
            if (pkg.one_time_price_display) {
              return parseFloat(pkg.one_time_price_display.replace(/[$,]/g, ''))
            }
            return 0
          }
          return getPrice(a) - getPrice(b)
        case 'popular':
        default:
          // Featured first, then popular, then by name
          if (a.is_featured && !b.is_featured) return -1
          if (!a.is_featured && b.is_featured) return 1
          if (a.is_popular && !b.is_popular) return -1
          if (!a.is_popular && b.is_popular) return 1
          return a.name.localeCompare(b.name)
      }
    })
  }, [packages, searchTerm, selectedType, sortBy])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Self-Hosting Service Packages
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose from our professional self-hosting solutions. Each package includes
              expert setup, ongoing support, and all the tools you need for digital independence.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Mobile: Stack search above filters */}
            <div className="w-full lg:hidden">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search packages..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Mobile: Horizontal scrolling filters */}
            <div className="w-full lg:hidden">
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300">
                <div className="flex items-center gap-2 min-w-fit">
                  <FunnelIcon className="w-5 h-5 text-gray-400" />
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value as PackageTypeFilter)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-fit"
                  >
                    {packageTypeFilters.map((filter) => (
                      <option key={filter.value} value={filter.value}>
                        {filter.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-2 min-w-fit">
                  <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Sort by:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'name' | 'price' | 'popular')}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-fit"
                  >
                    <option value="popular">Featured & Popular</option>
                    <option value="name">Name</option>
                    <option value="price">Price</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Desktop: Original layout */}
            <div className="hidden lg:flex lg:flex-row gap-4 items-center justify-between w-full">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search packages..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Type Filter */}
              <div className="flex items-center gap-2">
                <FunnelIcon className="w-5 h-5 text-gray-400" />
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value as PackageTypeFilter)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {packageTypeFilters.map((filter) => (
                    <option key={filter.value} value={filter.value}>
                      {filter.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Sort by:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'name' | 'price' | 'popular')}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="popular">Featured & Popular</option>
                  <option value="name">Name</option>
                  <option value="price">Price</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="flex items-center justify-between mb-6">
          <div className="text-gray-600">
            {isLoading ? (
              'Loading packages...'
            ) : (
              `Showing ${filteredAndSortedPackages.length} of ${packages.length} packages`
            )}
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
              <p className="text-red-600 mb-4">Failed to load packages</p>
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
            </div>
          </div>
        )}

        {/* No Results */}
        {!isLoading && !error && filteredAndSortedPackages.length === 0 && (
          <div className="text-center py-12">
            <div className="max-w-md mx-auto">
              <div className="text-gray-400 mb-4">
                <FunnelIcon className="w-12 h-12 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No packages found
              </h3>
              <p className="text-gray-600 mb-4">
                Try adjusting your filters or search terms.
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm('')
                  setSelectedType('all')
                }}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        )}

        {/* Packages Grid */}
        {!isLoading && !error && filteredAndSortedPackages.length > 0 && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
          >
            {filteredAndSortedPackages.map((pkg) => (
              <motion.div key={pkg.id} variants={itemVariants}>
                <PackageCard package={pkg} />
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Call to Action */}
        {!isLoading && !error && filteredAndSortedPackages.length > 0 && (
          <div className="mt-16 text-center">
            <div className="bg-primary-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-primary-900 mb-4">
                Don't see what you need?
              </h2>
              <p className="text-primary-700 mb-6 max-w-2xl mx-auto">
                We can create custom self-hosting solutions tailored to your specific requirements.
                Contact us for a consultation and custom quote.
              </p>
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-primary-600 text-white hover:bg-primary-700"
                >
                  Get Custom Quote
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}